/**
 * 실행계획 보관함 — 저장해 둔 계획을 찾아 다시 열거나 내보낸다.
 *
 * **AI 와 무관하다.** 서비스도 `services/planArchive` 를 쓰고 `assistantClient` 를 거치지 않는다 —
 * AI 제외 빌드에서도 그대로 돌아야 한다. 「튜닝 사례로 등록」 버튼만 AI 가 있는 빌드에서 붙는다.
 *
 * 창의 모양·Esc·포커스는 `common/AppModal` 이 맡는다(docs/ui-modal-consistency.md).
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FiBookmark, FiDownload, FiTrash2, FiExternalLink, FiSearch, FiLoader } from 'react-icons/fi';
import AppModal from './common/AppModal';
import { useConfirm } from './ConfirmDialog';
import { listPlanArchive, getPlanArchive, deletePlanArchive } from '../services/planArchive';
import { serializePlanArchive, planArchiveToMarkdown } from '../utils/planArchiveFile';
import { formatApiError } from '../utils/formatters';
import { storedTime } from '../utils/storedTime';
import * as guardrailApi from '../services/guardrail';
import { guardExport } from '../utils/guardrailGate';
import { toast } from 'react-toastify';
import './PlanArchiveModal.css';

/** 계획 앞머리만 — 미리보기는 통째로 그릴 필요가 없다 */

function PlanArchiveModal({ open, onClose, onOpenInTab, onRegisterCase }) {
    const { t } = useTranslation();
    const confirm = useConfirm();

    const [groups, setGroups] = useState([]);
    const [q, setQ] = useState('');
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);

    const reload = useCallback(async (query = '') => {
        setLoading(true);
        try {
            const r = await listPlanArchive({ q: query });
            setGroups(r.groups || []);
        } catch (e) {
            toast.error(formatApiError(e, t));
            setGroups([]);
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (!open) return;
        setSelectedId(null);
        setDetail(null);
        reload('');
    }, [open, reload]);

    // 고른 것의 본문을 불러온다 — 목록에는 계획 원문이 없다(일부러 안 싣는다)
    useEffect(() => {
        if (!selectedId) { setDetail(null); return; }
        let alive = true;
        setDetailLoading(true);
        getPlanArchive(selectedId)
            .then((r) => { if (alive) setDetail(r.group || null); })
            .catch((e) => { if (alive) { toast.error(formatApiError(e, t)); setDetail(null); } })
            .finally(() => { if (alive) setDetailLoading(false); });
        return () => { alive = false; };
    }, [selectedId, t]);

    const handleSearch = (e) => {
        const v = e.target.value;
        setQ(v);
        reload(v);
    };

    const download = useCallback(async (group, format) => {
        const md = format === 'md';
        let body = md ? planArchiveToMarkdown(group) : serializePlanArchive(group);
        // 가드레일 4 — 읽으라고 내보내는 리포트(md)만 검사한다. .livesqlplan 은 다시 가져오는 교환 파일이라
        // 값을 바꾸면 가져온 계획이 원본과 달라진다(그대로 둔다 — docs/features/21-ai-guardrail.md)
        if (md) {
            const g = await guardExport(body, { scan: guardrailApi.scanText, redact: guardrailApi.redactText });
            if (!g) return;
            if (g.mode === 'unchecked') toast.warning(t('common:grd.exportUnchecked'));
            body = g.text;
        }
        const blob = new Blob([body], {
            type: md ? 'text/markdown;charset=utf-8' : 'application/json;charset=utf-8',
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const stamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        link.download = `${group.title}_${stamp}.${md ? 'md' : 'livesqlplan'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(link.href);
    }, [t]);

    const handleDelete = useCallback(async (group) => {
        const ok = await confirm({
            title: t('common:planArchive.deleteTitle'),
            message: t('common:planArchive.deleteBody', { title: group.title }),
            confirmText: t('common:ui.delete'),
            variant: 'danger',
        });
        if (!ok) return;
        try {
            await deletePlanArchive(group.id);
            setSelectedId(null);
            setDetail(null);
            reload(q);
            toast.success(t('common:planArchive.deleted'));
        } catch (e) {
            toast.error(formatApiError(e, t));
        }
    }, [confirm, q, reload, t]);

    // **잘라서 보이지 않는다**(2026-10-07) — 40줄만 보여 「계획 일부만 저장됐다」 로 읽혔다. 저장된 원문 전체가
    // 스크롤로 보이고, SQL Monitor 리포트가 있으면 이어서 붙인다.
    const firstItem = detail && detail.items && detail.items[0];
    const preview = firstItem
        ? [firstItem.planText || '',
           firstItem.monitorReport ? `\n\n===== ${t('common:plan.monitorReport')} =====\n\n${firstItem.monitorReport}` : '',
          ].join('')
        : '';

    return (
        <AppModal
            open={open}
            onClose={onClose}
            title={t('common:planArchive.title')}
            windowed
            icon={<FiBookmark size={14} />}
            className="parch-modal"
        >
            <div className="parch-body">
                <div className="parch-search">
                    <FiSearch size={12} />
                    <input
                        type="text"
                        value={q}
                        onChange={handleSearch}
                        placeholder={t('common:planArchive.searchPlaceholder')}
                    />
                </div>

                <div className="parch-split">
                    <div className="parch-list">
                        {loading && <div className="parch-empty"><FiLoader size={12} /> {t('common:state.loading')}</div>}
                        {!loading && groups.length === 0 && (
                            <div className="parch-empty">{t('common:planArchive.empty')}</div>
                        )}
                        {groups.map((g) => (
                            <button
                                type="button"
                                key={g.id}
                                className={`parch-item${selectedId === g.id ? ' active' : ''}`}
                                onClick={() => setSelectedId(g.id)}
                            >
                                <div className="parch-item-title">{g.title}</div>
                                <div className="parch-item-meta">
                                    <span>{storedTime(g.createdAt)}</span>
                                    {g.connName && <span className="parch-chip">{g.connName}</span>}
                                    {g.kind === 'pair' && <span className="parch-chip parch-chip-pair">{t('common:planArchive.kindPair')}</span>}
                                    {g.source === 'agent' && <span className="parch-chip">{t('common:planArchive.fromAgent')}</span>}
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="parch-detail">
                        {!selectedId && <div className="parch-empty">{t('common:planArchive.pickHint')}</div>}
                        {selectedId && detailLoading && <div className="parch-empty"><FiLoader size={12} /> {t('common:state.loading')}</div>}
                        {selectedId && !detailLoading && detail && (
                            <>
                                <div className="parch-detail-head">
                                    <div className="parch-detail-title">{detail.title}</div>
                                    <div className="parch-item-meta">
                                        <span>{storedTime(detail.createdAt)}</span>
                                        {detail.items[0] && detail.items[0].connName && (
                                            <span className="parch-chip">
                                                {detail.items[0].connName}
                                                {detail.items[0].dbType ? ` · ${detail.items[0].dbType}` : ''}
                                            </span>
                                        )}
                                    </div>
                                    {detail.summary && <div className="parch-summary">{detail.summary}</div>}
                                    {detail.note && <div className="parch-note">{detail.note}</div>}
                                </div>
                                <pre className="parch-preview">{preview}</pre>
                            </>
                        )}
                    </div>
                </div>

                <div className="parch-actions">
                    <button
                        type="button"
                        className="app-btn app-btn-primary"
                        disabled={!detail}
                        onClick={() => { onOpenInTab(detail); onClose(); }}
                    >
                        <FiExternalLink size={12} /> {t('common:planArchive.openInTab')}
                    </button>
                    <button type="button" className="app-btn" disabled={!detail} onClick={() => download(detail, 'md')}>
                        <FiDownload size={12} /> .md
                    </button>
                    <button type="button" className="app-btn" disabled={!detail} onClick={() => download(detail, 'json')}>
                        <FiDownload size={12} /> .livesqlplan
                    </button>
                    {/* AI 가 있는 빌드에서만 — 사례 기능 자체가 그 빌드에만 있다 */}
                    {onRegisterCase && (
                        <button type="button" className="app-btn" disabled={!detail} onClick={() => onRegisterCase(detail)}>
                            {t('common:planArchive.registerCase')}
                        </button>
                    )}
                    <button
                        type="button"
                        className="app-btn app-btn-danger parch-delete"
                        disabled={!detail}
                        onClick={() => handleDelete(detail)}
                    >
                        <FiTrash2 size={12} /> {t('common:ui.delete')}
                    </button>
                </div>
            </div>
        </AppModal>
    );
}

PlanArchiveModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    /** 보관물을 실행계획 탭으로 연다 */
    onOpenInTab: PropTypes.func.isRequired,
    /** AI 빌드에서만 넘어온다 — 없으면 버튼이 서지 않는다 */
    onRegisterCase: PropTypes.func,
};

export default PlanArchiveModal;
